import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
  Link,
} from "@mui/material";

import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ContentSparkLineBefore from "./content-before.svg?react";
import PhysicalSparkLineBefore from "./physical-before.svg?react";
import SentimentSparkLineBefore from "./sentiment-before.svg?react";
import WebsiteSparkLineBefore from "./website-before.svg?react";

import ContentSparkLineAfter from "./content-after.svg?react";
import PhysicalSparkLineAfter from "./physical-after.svg?react";
import SentimentSparkLineAfter from "./sentiment-after.svg?react";
import WebsiteSparkLineAfter from "./website-after.svg?react";

export function BeforeFindings({
  onSetHighlightedSection,
}: {
  onSetHighlightedSection: (section: string) => void;
}) {
  const theme = useTheme();

  return (
    <Grid
      container
      spacing={2}
      columnSpacing={{ xs: 2, md: 3 }}
      sx={{
        mb: 4,
      }}
    >
      {/* Sentiment */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <SentimentSparkLineBefore />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Sentiment
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("reviews" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Mixed reviews detected
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("social_media" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Social media presence found
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Sentiment */}

      {/* Content */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <ContentSparkLineBefore />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Content
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("website_content" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Prohibited web content
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("website_policy_analysis" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Anomalous privacy policy
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Content */}

      {/* Website Operations */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <WebsiteSparkLineBefore />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Website Operations
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("contact_info" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Consistent email and web domain
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("webserver_analysis" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Meaningful technology spend
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("website_traffic" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Website traffic found
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Website Operations */}

      {/* Physical Operations */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <PhysicalSparkLineBefore />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Physical Operations
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("connected_entities" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Connected entities
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("contact_info" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> All phone numbers valid
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("industry" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Industry classified
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Physical Operations */}
    </Grid>
  );
}

export function AfterFindings({
  onSetHighlightedSection,
}: {
  onSetHighlightedSection: (section: string) => void;
}) {
  const theme = useTheme();

  return (
    <Grid
      container
      spacing={2}
      columnSpacing={{ xs: 2, md: 3 }}
      sx={{
        mb: 4,
      }}
    >
      {/* Sentiment */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <SentimentSparkLineAfter />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Sentiment
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.error.main}
                onClick={() => {
                  onSetHighlightedSection("news_articles" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CancelOutlinedIcon /> Concerning news articles detected
              </Link>
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("reviews" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Mixed reviews detected
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("social_media" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Social media presence found
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Sentiment */}

      {/* Content */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <ContentSparkLineAfter />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Content
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("website_content" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Prohibited web content
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("website_policy_analysis" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Anomalous privacy policy
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Content */}

      {/* Website Operations */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <WebsiteSparkLineAfter />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Website Operations
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("contact_info" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Consistent email and web domain
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("webserver_analysis" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Meaningful technology spend
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("website_traffic" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Website traffic found
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Website Operations */}

      {/* Physical Operations */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Paper style={{ minHeight: "425px" }}>
          <Stack
            sx={{
              alignItems: "center",
              padding: 3,
            }}
          >
            <PhysicalSparkLineAfter />

            <Typography
              variant="h5"
              sx={{
                mt: 2,
                textAlign: "center",
              }}
            >
              Physical Operations
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.grey[500],
                textTransform: "uppercase",
                mt: 4,
              }}
            >
              Factors
            </Typography>
            <Stack
              sx={{
                gap: "0.5em",
                mt: 1.5,
              }}
            >
              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.warning.main}
                onClick={() => {
                  onSetHighlightedSection("connected_entities" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <InfoOutlinedIcon /> Connected entities
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("contact_info" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> All phone numbers valid
              </Link>

              <Link
                component={Box}
                style={{ cursor: "pointer" }}
                color={theme.palette.success.main}
                onClick={() => {
                  onSetHighlightedSection("industry" as any);
                }}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "0.25em",
                }}
              >
                <CheckCircleOutlinedIcon /> Industry classified
              </Link>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* END Physical Operations */}
    </Grid>
  );
}
